import { useEffect, useMemo, useRef } from "react";
import { useWorkspaceQuery } from "../../queries/useWorkspaceQuery";
import { useUnifiedStore } from "../../store/StoreContext";
import { GEO_DIAL_CODES, usePricingCountry } from "../../store/pricingCountry";
import { LOCATION_UNSELECTED } from "../../store/slices/doctorSelectionSlice";
import { AuthService } from "../../services/AuthService";
import { collectWorkspacePublicProfiles } from "../../services/PublicProfileService";
import { detectBrowserTimezone, normalizeTimezone, } from "../../utils/timezone";
import { ConsultationMode } from "../../constants";
import { buildWorkspaceDisplayMap, buildManualDisplayMap, buildEnrichedDisplayMap, computeVisibleDoctorIds, collectWorkspaceDoctors, resolveDoctorDisplayMode, resolveSingleDoctorExternalId, } from "../appointment/utils/doctorDisplay";
import { getVisibleLocations, getVisibleDoctorsForAddress, findFirstVisibleDoctorInAddress, } from "../appointment/utils/workspaceDoctors";
const PROFILE_GATE_MODES = new Set([
    "multi",
    "single",
    "manual",
]);
export function WorkspaceLoader({ onError }) {
    const pricingCountryCode = usePricingCountry();
    const otpVerified = useUnifiedStore((s) => s.otpVerified);
    const { data: workspace, isLoading: workspaceIsLoading, isPlaceholderData: repricing, error: workspaceQueryError, refetch: refetchWorkspace, } = useWorkspaceQuery(pricingCountryCode, {
        keepPreviousWhileRepricing: otpVerified,
    });
    const setPricingRefreshPending = useUnifiedStore((s) => s.setPricingRefreshPending);
    useEffect(() => {
        setPricingRefreshPending(repricing);
    }, [repricing, setPricingRefreshPending]);
    const profiles = useMemo(() => (workspace ? collectWorkspacePublicProfiles(workspace) : null), [workspace]);
    const setWorkspace = useUnifiedStore((s) => s.setWorkspace);
    const setWorkspaceLoading = useUnifiedStore((s) => s.setWorkspaceLoading);
    const setWorkspaceError = useUnifiedStore((s) => s.setWorkspaceError);
    const setGeoLocation = useUnifiedStore((s) => s.setGeoLocation);
    const setDoctorDisplayMap = useUnifiedStore((s) => s.setDoctorDisplayMap);
    const setVisibleDoctorIds = useUnifiedStore((s) => s.setVisibleDoctorIds);
    const setRetryLoad = useUnifiedStore((s) => s.setRetryLoad);
    const selectDoctor = useUnifiedStore((s) => s.selectDoctor);
    const setLocationFilter = useUnifiedStore((s) => s.setLocationFilter);
    const applyDetectedTimezone = useUnifiedStore((s) => s.applyDetectedTimezone);
    const setCountryCode = useUnifiedStore((s) => s.setCountryCode);
    const selectedDoctorId = useUnifiedStore((s) => s.selectedDoctor?.id ?? null);
    const selectedAddressId = useUnifiedStore((s) => s.selectedAddressId);
    const selectionRef = useRef({ selectedDoctorId, selectedAddressId });
    useEffect(() => {
        selectionRef.current = { selectedDoctorId, selectedAddressId };
    }, [selectedDoctorId, selectedAddressId]);
    const verifiedRef = useRef(otpVerified);
    useEffect(() => {
        verifiedRef.current = otpVerified;
    }, [otpVerified]);
    useEffect(() => {
        setWorkspaceLoading(workspaceIsLoading);
    }, [workspaceIsLoading, setWorkspaceLoading]);
    useEffect(() => {
        if (workspaceQueryError)
            onError?.(workspaceQueryError);
    }, [workspaceQueryError, onError]);
    useEffect(() => {
        setWorkspaceError(workspaceQueryError && !workspace
            ? workspaceQueryError.message || "Failed to load workspace"
            : null);
    }, [workspaceQueryError, workspace, setWorkspaceError]);
    useEffect(() => {
        const retry = () => {
            setWorkspaceError(null);
            setVisibleDoctorIds(null);
            void refetchWorkspace();
        };
        setRetryLoad(retry);
        return () => setRetryLoad(null);
    }, [refetchWorkspace, setRetryLoad, setVisibleDoctorIds, setWorkspaceError]);
    const defaultsAppliedRef = useRef(false);
    useEffect(() => {
        if (!workspace)
            return;
        setWorkspace(workspace);
        const geo = AuthService.getGeoLocation();
        setGeoLocation(geo);
        if (!defaultsAppliedRef.current) {
            defaultsAppliedRef.current = true;
            const timezone = detectBrowserTimezone() || normalizeTimezone(geo?.timezone);
            if (timezone) {
                applyDetectedTimezone(timezone);
            }
            const geoDialCode = geo?.country
                ? GEO_DIAL_CODES[geo.country.toUpperCase()]
                : undefined;
            if (geoDialCode && !verifiedRef.current) {
                setCountryCode(geoDialCode);
            }
        }
        const mode = resolveDoctorDisplayMode();
        const baseMap = mode === "manual"
            ? buildManualDisplayMap(workspace)
            : buildWorkspaceDisplayMap(workspace);
        setDoctorDisplayMap(baseMap);
    }, [
        workspace,
        setWorkspace,
        setGeoLocation,
        applyDetectedTimezone,
        setCountryCode,
        setDoctorDisplayMap,
    ]);
    useEffect(() => {
        if (!workspace || !profiles)
            return;
        const mode = resolveDoctorDisplayMode();
        const gated = PROFILE_GATE_MODES.has(mode);
        let visibleIds = gated
            ? computeVisibleDoctorIds(workspace, profiles)
            : collectWorkspaceDoctors(workspace).map((d) => d.id);
        const singleDoctorExternalId = resolveSingleDoctorExternalId();
        if (singleDoctorExternalId !== null) {
            const matchedId = collectWorkspaceDoctors(workspace).find((d) => d.externalUserId === singleDoctorExternalId)?.id ?? null;
            visibleIds =
                matchedId !== null ? visibleIds.filter((id) => id === matchedId) : [];
        }
        setVisibleDoctorIds(visibleIds);
        if (mode !== "manual") {
            const enriched = buildEnrichedDisplayMap(workspace, profiles, buildWorkspaceDisplayMap(workspace));
            setDoctorDisplayMap(enriched);
        }
        const { selectedDoctorId: keptDoctorId, selectedAddressId: keptAddressId } = selectionRef.current;
        if (keptDoctorId !== null && keptAddressId !== null) {
            const kept = getVisibleDoctorsForAddress(workspace, keptAddressId, visibleIds).find((d) => d.id === keptDoctorId);
            if (kept) {
                selectDoctor(kept, keptAddressId);
                return;
            }
        }
        const visibleLocations = getVisibleLocations(workspace, visibleIds);
        if (visibleLocations.length > 0) {
            const addressId = visibleLocations[0].id;
            setLocationFilter(String(addressId));
            const doctor = findFirstVisibleDoctorInAddress(workspace, addressId, visibleIds, ConsultationMode.OFFLINE);
            if (doctor)
                selectDoctor(doctor, addressId);
        }
        else {
            setLocationFilter(LOCATION_UNSELECTED);
        }
    }, [
        workspace,
        profiles,
        setVisibleDoctorIds,
        setDoctorDisplayMap,
        setLocationFilter,
        selectDoctor,
    ]);
    return null;
}
